import { useCallback, useEffect } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { CoreFormFieldText } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldText';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  MomentmBrokerAuthRead,
  useBrokersMomentmBrokerAuthCreateMutation,
  useBrokersMomentmBrokerAuthRetrieveQuery,
  useBrokersMomentmBrokerAuthUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { adminActions } from '../admin.slice';
import { TimezoneSelectFormField } from './TimezoneSelectFormField';

interface MomentmSettingsFormProps {
  onSubmit: SubmitHandler<MomentmBrokerAuthRead>;
  onCancel: () => void;
}

export function MomentmSettingsForm({
  onSubmit,
  onCancel,
}: MomentmSettingsFormProps) {
  const form = useFormContext<MomentmBrokerAuthRead>();

  return (
    <div className="flex flex-col gap-2">
      <Controller
        control={form.control}
        name="username"
        rules={{
          required: 'This field is required',
        }}
        render={({ field, fieldState }) => (
          <CoreFormFieldText
            {...field}
            label="Username *"
            inputProps={{
              ...field,
              id: 'username',
            }}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={form.control}
        name="broker_name"
        rules={{
          required: 'This field is required',
        }}
        render={({ field, fieldState }) => (
          <CoreFormFieldText
            {...field}
            label="Broker Name *"
            inputProps={{
              ...field,
              id: 'broker_name',
            }}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={form.control}
        name="payer"
        rules={{
          required: 'This field is required',
        }}
        render={({ field, fieldState }) => (
          <CoreFormFieldText
            {...field}
            label="Payer * (uuid)"
            inputProps={{
              ...field,
              id: 'payer',
            }}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={form.control}
        name="api_url"
        rules={{
          required: 'This field is required',
        }}
        render={({ field, fieldState }) => (
          <CoreFormFieldText
            {...field}
            label="API URL"
            inputProps={{
              ...field,
              id: 'api_url',
            }}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={form.control}
        name="broker_time_zone_name"
        rules={{
          required: 'This field is required',
        }}
        render={({ field, fieldState }) => (
          <TimezoneSelectFormField
            {...field}
            value={{ label: field.value || '', value: field.value || '' }}
            label="Broker Time Zone Name *"
            inputProps={{
              ...field,
              id: 'broker_time_zone_name',
            }}
            onChange={(option) => {
              field.onChange(option.value);
            }}
            error={fieldState.error?.message}
          />
        )}
      />
      <div className="flex flex-row justify-between">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={form.handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}

interface MomentmSettingsFormControllerProps {
  settingsId?: string | null;
}

export function MomentmSettingsFormController({
  settingsId,
}: MomentmSettingsFormControllerProps) {
  const dispatch = useAppDispatch();
  const org = useAppSelector((state) => state.auth.currentOrganizationId);
  const form = useForm<MomentmBrokerAuthRead>({
    defaultValues: {
      username: '',
      broker_name: 'welltrans',
      payer: '',
      api_url: '',
      broker_time_zone_name: '',
      organization: org,
    },
  });

  const { data: settingsData } = useBrokersMomentmBrokerAuthRetrieveQuery(
    {
      id: settingsId!,
    },
    {
      skip: !settingsId,
    }
  );

  const [updateSettings] = useBrokersMomentmBrokerAuthUpdateMutation();
  const [createSettings] = useBrokersMomentmBrokerAuthCreateMutation();

  useEffect(() => {
    if (settingsData) {
      form.reset({
        ...settingsData,
      });
    }
  }, [form, settingsData]);

  const handleCancel = useCallback(() => {
    form.reset();
    dispatch(adminActions.setShowMomentmSettingsModal(false));
    dispatch(adminActions.setEditingMomentmSettingsId(null));
  }, [dispatch, form]);

  const handleSubmit = useCallback<SubmitHandler<MomentmBrokerAuthRead>>(
    async (data) => {
      try {
        if (settingsId) {
          await updateSettings({
            id: settingsId,
            momentmBrokerAuth: data,
          }).unwrap();
        } else {
          await createSettings({ momentmBrokerAuth: data }).unwrap();
        }
        dispatch(
          show({
            title: 'Successfully saved settings',
            type: 'success',
          })
        );
        handleCancel();
      } catch (e) {
        dispatch(
          show({
            title: 'Failed to save settings',
            description: formatServerError(e),
            type: 'error',
          })
        );
      }
    },
    [createSettings, dispatch, handleCancel, settingsId, updateSettings]
  );

  return (
    <FormProvider {...form}>
      <MomentmSettingsForm onSubmit={handleSubmit} onCancel={handleCancel} />
    </FormProvider>
  );
}
